import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Product } from '../interfaces';
import {
  hasIsraelLawAgreement,
  isIsraelLawFeatureEnabled,
  isIsraelLawFlight,
} from '../userAgreements';
import type { ShoppingCartV2 } from '../ShoppingCartSliceV2';

/**
 * Config-gated access to the Israeli-law agreement flow.
 *
 * `siteConfig.shareB2C.enable_isreal_law` switches the whole feature: every
 * entry point that tags a flight (`is_israel_law_enabled` in the add-to-cart
 * payloads) and every UI surface (cart badge, flight-page section, save /
 * copy-offer notices) goes through these helpers, so with the switch off they
 * all answer `false` and nothing is tagged, shown or confirmed.
 */
const useIsraelLawAgreement = () => {
  const siteConfig = useSelector(
    (state: RootState) => state.config.config?.siteConfig
  );
  const enabled = isIsraelLawFeatureEnabled(siteConfig);

  return useMemo(
    () => ({
      enabled,
      /** A flight view response (`{ flightsInfo, pax, is_israel_law_enabled }`). */
      isIsraelLawFlight: (flightResponse?: { is_israel_law_enabled?: boolean }) =>
        enabled && isIsraelLawFlight(flightResponse),
      /** A cart product, or any of its sub-products. */
      hasIsraelLawAgreement: (product?: Product) =>
        enabled && hasIsraelLawAgreement(product),
      /** The cart's order-level agreements hold at least one product. */
      cartHasIsraelLawAgreement: (cart?: Partial<ShoppingCartV2> | null) =>
        enabled && !!cart?.userAgreements?.products_with_agreements?.length,
    }),
    [enabled]
  );
};

export default useIsraelLawAgreement;
